import { debounce } from '../../utilities/helpers';

const SELECTORS = {
    galleryParent: '#jsArtworkGallery',
    modal: '#artworkChangeModal',
    titleSelect: '#find-artwork-title',
    categorySelect: '#find-artwork-category',
    itemSection: '.js-gallery-section',
    item: '.js-gallery-item'
};

const LOADING_HTML = '<div class="text-center"><i class="fa-solid fa-circle-notch fa-spin fa-2x"></i><br><p>Loading...</p></div>';

// ==========================================================================
// SECTION 1: ARTWORK FILTERING LOGIC
// ==========================================================================

/**
 * @function initArtworkFilterHandlers
 * @description Main entry point. Attaches event handlers for search, category, and reset.
 */
export function initArtworkFilterHandlers() {
    const $galleryParent = $(SELECTORS.galleryParent);

    initArtworkTitleSearch();
    initArtworkCategoryFilter();

    // Reset Button Handler
    $galleryParent.off('click', '.js-filter-reset').on('click', '.js-filter-reset', (evt) => {
        evt.preventDefault();
        clearFilters();
    });
}

/**
 * @function filterArtwork
 * @description Filters the gallery. 
 * LOGIC: Logic relies on Inputs being Mutually Exclusive (enforced by event handlers).
 * - If Category is set: Title input should be empty.
 * - If Title is set (or typing): Category input should be 'all'.
 */
function filterArtwork() {
    const $titleSelect = $(SELECTORS.titleSelect);
    const selectedCategoryVal = $(SELECTORS.categorySelect).val();
    let hasVisibleSections = false;
    const searchText = $('.art-title-dropdown .select2-search__field').val() ?? '';
    const hasSearchText = !!searchText.trim().length;

    // 1. Determine Target Category
    const categoryTargetId = (!selectedCategoryVal || selectedCategoryVal === 'all')
        ? 'all'
        : `section-${selectedCategoryVal}`;

    // 2. Get Selected Titles
    // PRIORITY: Check if the input has a real value (User clicked an option)
    let selectedTitleIds = [];
    let isTitleFilterActive = false;
    const directValue = $titleSelect.val();
    if (directValue && directValue.length > 0) {
        selectedTitleIds = Array.isArray(directValue) ? directValue : [directValue];
        isTitleFilterActive = true;
    } else {
        // User is typing (no selection made yet), scrape the visible Select2 results
        const $visibleSelect2Options = $('.art-title-dropdown .select2-results__option[aria-selected]');
        $visibleSelect2Options.each(function () {
            const data = $(this).data('data');
            if (data && data.id) {
                selectedTitleIds.push(data.id);
            }
        });
    }

    // 3. Loop through sections and items to toggle visibility
    $(SELECTORS.itemSection).each(function () {
        const $section = $(this);
        const sectionId = $section.attr('id');

        // Check 1: Does section match the category?
        if (categoryTargetId !== 'all' && sectionId !== categoryTargetId) {
            $section.hide();
            return; // Skip processing items in hidden sections
        }

        // Check 2: Process items inside the section
        let visibleItemsCount = 0;
        const $items = $section.find(SELECTORS.item);

        $items.each(function () {
            const $item = $(this);
            const itemRawId = $item.attr('id').replace('item-', '');

            // Match if NO titles are active OR if item ID is in the active list
            const isTitleMatch = (selectedTitleIds.length === 0 && !hasSearchText) || selectedTitleIds.includes(itemRawId);

            if (isTitleMatch) {
                $item.show();
                visibleItemsCount++;
            } else {
                $item.hide();
            }
        });

        // Check 3: Hide section header if all items inside are hidden
        if(visibleItemsCount > 0) { 
            $section.show();
            hasVisibleSections = true;
        } else { 
            $section.hide();
        }
    });

    const $noResultsMsg = $('#js-no-results-message');
    
    if (!hasVisibleSections && hasSearchText) {
        $noResultsMsg.fadeIn(200);
    } else {
        $noResultsMsg.hide();
    }
}

function clearFilters() {
    $(SELECTORS.categorySelect).val('all'); // Reset Category
    $(SELECTORS.titleSelect).val(null).trigger('change'); // Reset Title
    filterArtwork();
}

/**
 * @function initArtworkTitleSearch
 * @description Initializes Select2 and binds the "Type-to-Filter" logic.
 * ENFORCES: If Title is used, Category is reset to "All".
 */
const initArtworkTitleSearch = () => {
    const $titleSearch = $(SELECTORS.titleSelect);
    if (!$titleSearch.length) return;

    // --- RE-INIT LOGIC ---
    // 1. Check if the actual Select2 data instance exists before destroying
    if ($titleSearch.data('select2')) {
        $titleSearch.select2('destroy');
    }

    // 2. Handle "Zombie" classes: If the class exists but data was missing, 
    // we must manually remove the class or Select2 won't re-init properly.
    if ($titleSearch.hasClass('select2-hidden-accessible')) {
        $titleSearch.removeClass('select2-hidden-accessible');
        // Optional: Remove any orphaned container sitting next to it
        $titleSearch.next('.select2-container').remove();
    }

    // Clean up previous event listeners
    $titleSearch.off('select2:select select2:unselect');

    // Remove global document listener
    $(document).off('input', '.art-title-dropdown .select2-search__field');

    const $dropdownParent = $titleSearch.closest('.modal-content').length
        ? $titleSearch.closest('.modal-content')
        : $('body');

    // Init Select2
    $titleSearch.select2({
        placeholder: "Select or type a title...",
        allowClear: true,
        dropdownParent: $dropdownParent,
        theme: 'bootstrap-5',
        dropdownCssClass: 'art-title-dropdown',
        closeOnSelect: true
    });

    // --- EVENT: Selection Made (User Clicked Option or Pressed Enter) ---
    $titleSearch.on('select2:select', function () {
        // MUTUAL EXCLUSIVITY: Reset Category to "All"
        $(SELECTORS.categorySelect).val('all');
        filterArtwork();
    });

    // --- EVENT: Unselect (User cleared the box) ---
    $titleSearch.on('select2:unselect', () => filterArtwork());

    // --- EVENT: Typing (Type-to-Filter) ---
    const debouncedFilterArtwork = debounce((searchText) => {
        // MUTUAL EXCLUSIVITY: If user types anything, Reset Category to "All"
        if(!!searchText) {
            $(SELECTORS.categorySelect).val('all');
        }
        
        filterArtwork();
    }, 300);

    $(document).on('input', '.art-title-dropdown .select2-search__field', function (e) {
        debouncedFilterArtwork($(this).val());
    });

    // Allow scrolling in modal when Select2 is open
    if ($dropdownParent.hasClass('modal-content')) {
        $dropdownParent.find('.modal-body').on('scroll', function () {
            if ($titleSearch.data('select2')?.isOpen()) {
                $titleSearch.select2('close');
                if (document.activeElement) document.activeElement.blur();
            }
        });
    }
}

/**
 * @function initArtworkCategoryFilter
 * @description Handles Category changes.
 * ENFORCES: If Category is used, Title is cleared.
 */
function initArtworkCategoryFilter() {
    $(SELECTORS.galleryParent).off('change', SELECTORS.categorySelect).on('change', SELECTORS.categorySelect, () => {
        // MUTUAL EXCLUSIVITY: Clear title filter when category changes
        // We use silent update if possible, but trigger change is safer for Select2 UI updates
        $(SELECTORS.titleSelect).val(null).trigger('change');
        filterArtwork();
    });
}

// ==========================================================================
// SECTION 2: MODAL & LEGACY FORM HANDLERS
// ==========================================================================

export function initCustomArtworkFormHandlers() {
    const $customForm = $('#custom_form');
    if (!$customForm.length) return;

    // 1. Validation
    if ($.fn.validate) {
        $customForm.validate();
    }

    // 2. Inject "Save" Button into Modal Footer if missing
    const $footer = $customForm.closest('.modal-content').find('.modal-footer');
    if (!$footer.find('#customArtwork, #addItemSubmit').length) {
        $footer.append(
            $('<button>', { id: 'customArtwork', type: 'submit', class: 'btn-primary', text: 'Save' })
        );
    }

    // 3. Handle Submit
    $footer.off('click', '#customArtwork').on('click', '#customArtwork', function () {
        if ($customForm.valid()) {
            $(this).prop('disabled', true).text('Saving...');
            $customForm.trigger('submit');
        }
    });

    // 4. File Input UI Enhancements
    initFileInputEnhancements();
}

/**
 * @function initFileInputEnhancements
 * @description Standard logic to make file inputs behave nicely in Bootstrap
 */
function initFileInputEnhancements() {
    $(document).off('change', '.btn-file :file').on('change', '.btn-file :file', function () {
        const input = $(this);
        const numFiles = input.get(0).files ? input.get(0).files.length : 1;
        const label = input.val().replace(/\\/g, '/').replace(/.*\//, '');
        input.trigger('fileselect', [numFiles, label]);
    });

    $(document).off('fileselect', '.btn-file :file').on('fileselect', '.btn-file :file', function (event, numFiles, label) {
        const input = $(this).parents('.input-group').find(':text');
        const log = numFiles > 1 ? numFiles + ' files selected' : label;
        if (input.length) input.val(log);
    });
}

export function copyFileNameToIdentifierInput() {

    $(document).off('change', '#customFile').on('change', '#customFile', function(e) {

        const fileList = e.target.files;
        const $inputField = $('[data-field="imageName"]');


        if (fileList && fileList.length > 0 && $inputField.length > 0) {
            let name = fileList[0].name;

            // Remove file extension (e.g., "image.png" -> "image")
            name = name.replace(/\.[^/.]+$/, "");

            if ($inputField.val().trim() === "") {
                $inputField.val(name);
            }
        }
    });
}

function initLegacyModalInteractions() {
    const $legacyModal = $(SELECTORS.modal);
    if (!$legacyModal.length) return;

    // Handle internal navigation clicks (Reload modal content)
    const triggerSelectors = '.js-go-to-custom, .js-custom-view-nav-cta, .js-upload-new, .js-modal-pagination .page-link';
    $legacyModal.off('click', triggerSelectors).on('click', triggerSelectors, function (evt) {
        evt.preventDefault();
        const $btn = $(this);

        // UI Feedback
        $btn.html('<i class="fa-solid fa-circle-notch fa-spin"></i> Loading...');

        loadAndInitModalContent($legacyModal, $btn.attr('href'));
    });

    // Handle "Choose this artwork" clicks
    $legacyModal.off('click', '.js-brand-artwork').on('click', '.js-brand-artwork', function (evt) {
        evt.preventDefault();
        const artworkId = $(this).data('brandartworkid');

        $(SELECTORS.galleryParent).find('#brandArtworkID').val(artworkId).trigger('change');
        $(SELECTORS.galleryParent).find('#choose-image').trigger('submit');
        $(this).closest('.modal-body').html('<div class="row editSubmit">Saving...</div>');
    });

    // Handle Dropdown changes that reload the modal (Sorting/Upload New)
    $legacyModal.off('change', '#customSort').on('change', '#customSort', function () {
        loadAndInitModalContent($legacyModal, $(this).val());
    });
}

function initShowUploadFieldsCta() {
    $(document).on('click', '.js-upload-commit', function (e) {
        e.preventDefault();
        const $btn = $(this);
        const $container = $btn.closest('.form-group').siblings('.customer-file-container');

        $btn.slideUp(200, () => $btn.remove());
        $container.slideDown(400).find('input, textarea, button').prop('disabled', false);
    });
}

// ==========================================================================
// SECTION 3: HELPERS
// ==========================================================================

/**
 * @description Centralized function to load AJAX content into the modal and re-init handlers.
 */
function loadAndInitModalContent($modal, loadUrl) {
    const $modalBody = $modal.find('.modal-body');
    $modalBody.html(LOADING_HTML);

    $modalBody.load(loadUrl, (response, status, xhr) => {
        if (status === "error") {
            console.error("Error loading modal content:", xhr.statusText);
            $modalBody.html('<div class="alert alert-danger">Failed to load custom art content.</div>');
            return;
        }

        // Fix Title
        const $h1 = $modalBody.find('h1').first();
        const $modalTitle = $modal.find('.modal-title');
        if ($h1.length && $modalTitle.length) {
            $modalTitle.text($h1.text().trim());
            $h1.hide();
        }

        // Re-Initialize all logic for the new content
        initArtworkFilterHandlers();
        initLegacyModalInteractions();
        initShowUploadFieldsCta();
        initCustomArtworkFormHandlers();
        copyFileNameToIdentifierInput();
    });
}

// ==========================================================================
// SECTION 4: INITIALIZATION
// ==========================================================================

$(function () {
    const $legacyModal = $(SELECTORS.modal);
    const triggerSelectors = '.art-upload-button, .art-reload-button, .art-order-review-change, .zone-edit-artwork, .js-modal-pagination .page-link';

    // Scenario A: Button on page opens the modal
    $(document).on('click', triggerSelectors, function (e) {
        e.preventDefault();
        if (!$legacyModal.length) return;

        const baseLoadUrl = $legacyModal.data('load-url');
        const itemArtworkID = $(this).data('itemartworkid') || '0';
        const finalUrl = baseLoadUrl.replace('ITEM_ARTWORK_ID', itemArtworkID);

        $legacyModal.modal('show');
        $legacyModal.one('shown.bs.modal', function () {
            loadAndInitModalContent($legacyModal, finalUrl);
        });
    });
    // Scenario B: Modal opened via data-bs-toggle (Standard Bootstrap)
    $legacyModal.on('shown.bs.modal', function (e) {
        if (!$(e.relatedTarget).is(triggerSelectors)) {
            // Assume content is already present, just init handlers
            initArtworkFilterHandlers();
            initLegacyModalInteractions();
            initShowUploadFieldsCta();
            initCustomArtworkFormHandlers();

        }
    });
});